import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import Experience from '../Experience'

export class Camera {
  constructor(props) {
    const { parallax = true, shouldFollowPage = false } = props || {}

    // Options
    this.parallax = parallax
    this.shouldFollowPageHeight = shouldFollowPage
    this.scrollY = window.scrollY

    // Resources
    this.experience = new Experience()
    this.sizes = this.experience.sizes
    this.scene = this.experience.scene
    this.canvas = this.experience.canvas
    this.mouse = this.experience.mouse
    this.time = this.experience.time
    this.debug = this.experience.debug

    // Debug
    if (this.debug.active) {
      this.debugFolder = this.debug.ui.addFolder('Camera')
      this.debugFolder.close()
    }

    // Setup
    this.setInstance()
    this.setBackgroundInstance()
    this.setScrollListeners()
    this.setOrbitControls()
  }

  setInstance() {
    // Camera Group (For Parallax)
    this.cameraGroup = new THREE.Group()
    this.scene.add(this.cameraGroup)

    // Perspective Camera
    this.cameraInitialPosition = new THREE.Vector3(0, 2.1, 7)
    this.instance = new THREE.PerspectiveCamera(50, this.sizes.width / this.sizes.height, 0.1, 50)
    this.instance.position.set(this.cameraInitialPosition.x, this.cameraInitialPosition.y, this.cameraInitialPosition.z)
    this.instance.lookAt(new THREE.Vector3(0, 0, 0))
    this.instance.updateProjectionMatrix()

    // Add Camera to Group
    this.cameraGroup.add(this.instance)
  }

  setBackgroundInstance() {
    this.backgroundInstance = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
  }

  setScrollListeners() {
    this._scrollHandler = () => {
      this.scrollY = window.scrollY
    }
    window.addEventListener('scroll', this._scrollHandler)
  }

  setOrbitControls() {
    if (!this.debug.active) return

    this.controls = new OrbitControls(this.instance, this.canvas)
    this.controls.enabled = false

    this.controls.enableZoom = false
    this.controls.minDistance = 4
    this.controls.maxDistance = 8

    this.controls.enableDamping = true
    this.controls.minPolarAngle = Math.PI / 4 // Sopra
    this.controls.maxPolarAngle = Math.PI / 2 // Sotto
    this.controls.minAzimuthAngle = -Math.PI / 4 // Sinistra
    this.controls.maxAzimuthAngle = Math.PI / 4 // Destra

    this.orbitControlsFolder = this.debugFolder.addFolder('orbit controls')

    this.orbitControlsFolder
      .add(this.controls, 'enabled')
      .name(`OC: enabled`)
      .onFinishChange(v => {
        this.controls.enabled = v
        this.parallax = !v
      })

    this.orbitControlsFolder
      .add(this.controls, 'enableZoom')
      .name(`OC: enableZoom`)
      .onFinishChange(v => (this.controls.enableZoom = v))

    this.orbitControlsFolder
      .add(this.controls, 'minDistance')
      .name(`OC: minDistance`)
      .min(1)
      .max(5)
      .step(1)
      .onFinishChange(v => (this.controls.minDistance = v))

    this.orbitControlsFolder
      .add(this.controls, 'maxDistance')
      .name(`OC: maxDistance`)
      .min(5)
      .max(30)
      .step(1)
      .onFinishChange(v => (this.controls.maxDistance = v))
  }

  resize() {
    this.instance.aspect = this.sizes.width / this.sizes.height
    this.instance.updateProjectionMatrix()
  }

  update() {
    if (this.controls && this.controls.enabled) this.controls.update()
    if (this.parallax) this.updateParallax()
    if (this.shouldFollowPageHeight) this.scrollWithPage()
  }

  updateParallax() {
    // Parallax movement
    const parallaxX = this.mouse.cursor.x
    const parallaxY = -this.mouse.cursor.y
    const deltaX = (parallaxX * 0.5 - this.cameraGroup.position.x) * 1 * this.time.delta
    const deltaY = (parallaxY * 0.5 - this.cameraGroup.position.y) * 1 * this.time.delta

    // Limit the parallax movement to a maximum of 0.5 units
    this.cameraGroup.position.x += Math.max(Math.min(deltaX, 0.5), -0.5)
    this.cameraGroup.position.y += Math.max(Math.min(deltaY, 0.5), -0.5)
  }

  scrollWithPage() {
    // Update Camera Position
    this.instance.position.y = this.cameraInitialPosition.y + (-this.scrollY / this.sizes.height) * 4
  }

  destroy() {
    window.removeEventListener('scroll', this._scrollHandler)
  }
}
